import { convertDayToNumber, convertTimeToMinutes } from '#lib/helper/date.js';
import { deepClone } from '#lib/helper/clone.js';
import { activityStore } from '#lib/stores/activity.js';
import { areaStore } from '#lib/stores/area.js';
import { businessStore } from '#lib/stores/business.js';
import { openingHourStore } from '#lib/stores/opening-hour.js';
import { openingHourExceptionDaysStore } from '#lib/stores/opening-hour-exception.js';
import { sessionStore } from '#lib/stores/session.js';
import {
	OpeningHourDay,
	OpeningHourEntityType,
	type Activity,
	type Area,
	type Business,
	type Offer,
	type OpeningHour,
	type OpeningHourException,
	type OpeningHourTime,
	type Session
} from '#lib/types/generated/index.js';
import type { DateTime } from 'luxon';
import { get } from 'svelte/store';

export const minDayHour = 300;
export const maxDayHour = 300 + 24 * 60;

export interface TimeslotMinute {
	start: number;
	end: number;
}

export const days: OpeningHourDay[] = [
	OpeningHourDay.Monday,
	OpeningHourDay.Tuesday,
	OpeningHourDay.Wednesday,
	OpeningHourDay.Thursday,
	OpeningHourDay.Friday,
	OpeningHourDay.Saturday,
	OpeningHourDay.Sunday
];

export const WEEK_OVERRIDE_CHOICE = {
	FOLLOW: 'follow',
	STANDARD: 'standard',
	CUSTOM: 'custom'
} as const;

type WeekOverrideChoice = (typeof WEEK_OVERRIDE_CHOICE)[keyof typeof WEEK_OVERRIDE_CHOICE];

export interface WeekOverrideChoiceItem {
	name: WeekOverrideChoice;
	icon: string;
}

export function cloneDefaultOpeningHourWeek(): OpeningHour | null {
	const defaultWeek = get(openingHourStore).find(
		(w) => w.type == 'default' && w.entityType == OpeningHourEntityType.Business
	);

	return defaultWeek ? deepClone(defaultWeek) : null;
}

function getDefaultOpeningHourWeek(): OpeningHour | undefined {
	return get(openingHourStore).find(
		(w) => w.type == 'default' && w.entityType == OpeningHourEntityType.Business
	);
}

export function sortOpeningHours(a: OpeningHour, b: OpeningHour): number {
	// first type=default, 2nd type=closed, then alphabetic order
	if (a.type == 'default' && b.type == 'default') return 0;
	if (a.type == 'default') return -1;
	if (b.type == 'default') return 1;
	if (a.type == 'closed' && b.type == 'closed') return 0;
	if (a.type == 'closed') return -1;
	if (b.type == 'closed') return 1;
	return a.name.localeCompare(b.name);
}

export function getOpeningHourWeekFor(
	entityType: OpeningHourEntityType,
	entityId: string | undefined | null,
	overrideWeek: OpeningHour | null
): OpeningHour | null {
	if (!overrideWeek) {
		overrideWeek = cloneDefaultOpeningHourWeek();
	}

	if (entityType == 'business' || entityId == null) {
		return overrideWeek;
	} else {
		const out = get(openingHourStore).find(
			(w) => w.override == overrideWeek?.id && w.entityId == entityId
		);
		if (out) return out;
		if (entityType == OpeningHourEntityType.Activity) {
			return getOpeningHourWeekFor(
				OpeningHourEntityType.Activity,
				get(activityStore).find((a) => a.id == entityId)?.parentId,
				overrideWeek
			);
		} else if (entityType == OpeningHourEntityType.Area) {
			return getOpeningHourWeekFor(
				OpeningHourEntityType.Activity,
				get(areaStore).find((a: Area) => a.id == entityId)?.parentId,
				overrideWeek
			);
		} else if (entityType == OpeningHourEntityType.Session) {
			return getOpeningHourWeekFor(
				OpeningHourEntityType.Activity,
				get(sessionStore).find((a) => a.id == entityId)?.parentId,
				overrideWeek
			);
		} else {
			const parentType = (overrideWeek?.entityType ??
				OpeningHourEntityType.Business) as OpeningHourEntityType;
			const parentId =
				parentType === OpeningHourEntityType.Activity
					? (overrideWeek?.entityId ?? null)
					: null;
			return getOpeningHourWeekFor(parentType, parentId, overrideWeek);
		}
	}

	return null;
}

export function isOpeningHourConcernedBy(
	entityType: OpeningHourEntityType,
	entityId: string,
	week: OpeningHour | OpeningHourException
): boolean {
	if (week.entityType == OpeningHourEntityType.Business) return true;
	if (week.entityId == entityId) return true;
	else if (entityType == OpeningHourEntityType.Area) {
		return isOpeningHourConcernedBy(
			OpeningHourEntityType.Activity,
			get(areaStore).find((a) => a.id == entityId)?.parentId!,
			week
		);
	} else if (entityType == OpeningHourEntityType.Session) {
		return isOpeningHourConcernedBy(
			OpeningHourEntityType.Activity,
			get(sessionStore).find((a) => a.id == entityId)?.parentId!,
			week
		);
	}
	return false;
}

export function isOfferConcernedByOpeningHour(offer: Offer, week: OpeningHour): boolean {
	return offer.sessionOrders.some((sessionOrder) =>
		sessionOrder.sessionIds.some((sId) =>
			isOpeningHourConcernedBy(OpeningHourEntityType.Session, sId, week)
		)
	);
}

export function getOpeningHourOverride(
	entityType: OpeningHourEntityType,
	entityId: string,
	week: OpeningHour
): OpeningHour | null {
	return (
		get(openingHourStore).find(
			(w) => w.override == week.id && w.entityId == entityId && w.entityType == entityType
		) ?? null
	);
}

export function searchExceptionOpeningHourForDate(
	date: DateTime,
	entity: Session | Area | Activity | Business,
	entityType:
		| OpeningHourEntityType.Business
		| OpeningHourEntityType.Activity
		| OpeningHourEntityType.Area
		| OpeningHourEntityType.Session
): OpeningHour | undefined {
	const daysStore = get(openingHourExceptionDaysStore) as Record<
		string,
		OpeningHourException[] | undefined
	>;
	const exception = daysStore[date.toISODate()!]?.find((e) => {
		return e.entityId === entity.id;
	});
	if (!exception) {
		let parentEntity: Area | Activity | Session | Business | undefined;
		switch (entityType) {
			case OpeningHourEntityType.Area:
				parentEntity = get(activityStore).find((a) => a.id === (entity as Area).parentId);
				entityType = OpeningHourEntityType.Activity;
				break;
			case OpeningHourEntityType.Activity:
				parentEntity = get(businessStore);
				entityType = OpeningHourEntityType.Business;
				break;
			case OpeningHourEntityType.Session:
				entityType = OpeningHourEntityType.Activity;
				parentEntity = get(activityStore).find(
					(a) => a.id === (entity as Session).parentId
				);
				break;
		}

		if (!parentEntity) return undefined;
		return searchExceptionOpeningHourForDate(date, parentEntity, entityType);
	}
	return get(openingHourStore).find((w) => w.id === exception.openingHourId);
}

// Return the opened timeslot for a session on a specific date
export function getTimeslotForSession(session: Session, date: DateTime): TimeslotMinute[] {
	const openingHour = getOpeningHourForSession(session, date);
	if (!openingHour) return [];

	const timeslot = openingHour.schedule.find((s) => convertDayToNumber(s.day) === date.weekday);
	if (!timeslot) return [];

	return timeslot.openingHours.map((t) => {
		return {
			start: convertTimeToMinutes(t.start, true),
			end: convertTimeToMinutes(t.end, true)
		};
	});
}

export function getTimeslotForArea(area: Area, date: DateTime): TimeslotMinute[] {
	const openingHour = getOpeningHourForArea(area, date);
	if (!openingHour) return [];

	const timeslot = openingHour.schedule.find((s) => convertDayToNumber(s.day) === date.weekday);
	if (!timeslot) return [];

	return timeslot.openingHours.map((t) => {
		return {
			start: convertTimeToMinutes(t.start, true),
			end: convertTimeToMinutes(t.end, true)
		};
	});
}

export function getOpeningHourForArea(area: Area, date: DateTime): OpeningHour | undefined {
	// Search for exception on this date (from bottom to top)
	const exception = searchExceptionOpeningHourForDate(date, area, OpeningHourEntityType.Area);
	// If exception, we are in not standard week, else we are in standard week

	// We search for the week to follow (from bottom to top)
	const out =
		getOpeningHourWeekFor(OpeningHourEntityType.Area, area.id, exception ?? null) ?? undefined;

	if (out?.scheduleFollowStandardWeek) {
		return getOpeningHourWeekFor(OpeningHourEntityType.Area, area.id, null) ?? undefined;
	}

	return out;
}

function getOpeningHourForSession(session: Session, date: DateTime): OpeningHour | undefined {
	// Search for exception on this date (from bottom to top)
	const exception = searchExceptionOpeningHourForDate(
		date,
		session,
		OpeningHourEntityType.Session
	);
	// If exception, we are in not standard week, else we are in standard week

	// We search for the week to follow (from bottom to top)
	const out =
		getOpeningHourWeekFor(OpeningHourEntityType.Session, session.id, exception ?? null) ??
		undefined;

	if (out?.scheduleFollowStandardWeek) {
		return getOpeningHourWeekFor(OpeningHourEntityType.Session, session.id, null) ?? undefined;
	}

	return out;
}

export function getOpeningHourForBusinessCalc(
	business: Business | null,
	date: DateTime
): OpeningHour | undefined {
	if (!business) return undefined;
	// Search for exception on this date
	const exception = searchExceptionOpeningHourForDate(
		date,
		business,
		OpeningHourEntityType.Business
	);
	if (exception) return exception;

	// Get the default opening hour for this business
	return getDefaultOpeningHourWeek();
}

export function getOpeningTimeslotsForDayArea(area: Area, date: DateTime): Array<TimeslotMinute> {
	let out: Array<OpeningHourTime> = [];
	const openingHour = getOpeningHourForArea(area, date);
	if (openingHour) {
		const timeslot = openingHour.schedule.find(
			(s) => convertDayToNumber(s.day) === date.weekday
		);
		if (timeslot) {
			out = timeslot.openingHours;
		}
	}

	return out.map((t) => {
		return {
			start: convertTimeToMinutes(t.start, true),
			end: convertTimeToMinutes(t.end, true)
		};
	});
}

export function getClosedTimeslotsForDayArea(area: Area, date: DateTime): Array<TimeslotMinute> {
	const out: Array<TimeslotMinute> = [];
	const openingHour = getOpeningTimeslotsForDayArea(area, date);

	let pivot = minDayHour;
	openingHour
		.sort((a, b) => a.start - b.start)
		.forEach((openTimeslot) => {
			out.push({
				start: pivot,
				end: openTimeslot.start
			});
			pivot = openTimeslot.end;
		});
	out.push({
		start: pivot,
		end: maxDayHour
	});

	return out;
}

export function isOpened(oh: OpeningHour | undefined, date: DateTime): boolean {
	if (!oh) return false;
	return oh.schedule.some(
		(s) => convertDayToNumber(s.day) === date.weekday && s.openingHours.length > 0
	);
}
